import { environment } from '../../../environments/environment';
import { CurrentUser } from '../../security/current-user.model';
import { Logging } from '../logging';
import { DataCollection } from './site-content/data-collection.model';

// See https://community-dc.max.gov/x/pYY3og for background on claims and how they're used to protect access to the application's pages/routes
// Added to model security claims for the current user
export class UserClaims {
    public claims: Set<string> = new Set();

    constructor(user: CurrentUser, site: DataCollection = null) {

        if (user.isSystemAdmin) {
            this.claims.add('CanViewAdmin');
            this.claims.add('CanViewAdminAbout');
            this.claims.add('CanViewAdminSettings');
            this.claims.add('CanViewAdminUserInfo');
            this.claims.add('CanViewAdminAllSites');
        }

        //VNEXT-787 in prod, members of COLLECT-VNEXT-PROD-SITE-CREATORS(NONFED)
        let apiUrl = environment.apiUrl.toLowerCase();
        if (apiUrl.indexOf('.test.') < 0 && apiUrl.indexOf('.stage.') < 0 && apiUrl.indexOf('.sandbox.') < 0) {
            if (user.isProdSiteCreator) {
                this.claims.add('CanCreateSite');
            }
        }
        else {
            //console.log('Not production; user.isSiteCreator is ' + user.isSiteCreator + ' and user.isSystemAdmin is ' + user.isSystemAdmin);
            //members of COLLECT-VNEXT-SITE-CREATORS
            //or COLLECT-VNEXT-SYSTEM-ADMINS
            if (user.isSiteCreator || user.isSystemAdmin) {
                this.claims.add('CanCreateSite');
            }
        }

        if (user.hasAccessibleSites || user.isSystemAdmin) {
            this.claims.add('CanViewSiteList');
        }

        if (site) {
            if (site.siteIsAdministerable) {
                //***Vnext-2010: Only display Site Structure menu for SL roles if they have IsSiteTreeViewable selected*/
                if ((true || site.isSiteTreeViewable) && site.hasStructureContentTemplate) // Per Sarah 03-05-2026:  "We want to get rid of that and just make the structure available to anyone, assuming there is a page set as the structure."
                    this.claims.add('CanViewSiteStructure');

                this.claims.add('CanViewSiteSummary');
                this.claims.add('CanViewSiteFormCatalog');
                this.claims.add('CanViewSitePermissions');
                this.claims.add('CanViewSiteWorkflowCatalog'); 
                this.claims.add('CanViewSiteManagement');
                this.claims.add('CanBuildConstraints');
                this.claims.add('CanBuildWorkflows');
                this.claims.add('CanPublish');
                this.claims.add('CanReorderForms');
                this.claims.add('canRenameForm');
                this.claims.add('isSiteTreeViewable'); //vnext-2010 confirm if this is truly needed.
            } else {
                // Note:  per VNEXT-1619, if the user is not an admin but can edit form template HTML fields only, add a claim for that.
                if (site.currentUserCanEditFormTemplateHtml)
                    this.claims.add('CanEditFormTemplateHtmlOnly');
            }
            if (site.currentUserCanGrantPermissions || site.currentUserCanManageRoleMembers) {
                this.claims.add('CanViewSitePermissions');
            }
            // Note:  discuss the next four lines with Paul before enabling them.
            //***Vnext-2010: Only display Site Structure menu for SL roles if they have IsSiteTreeViewable selected*/
            if (true || site.isSiteTreeViewable) { // Per Sarah 03-05-2026:  "We want to get rid of that and just make the structure available to anyone, assuming there is a page set as the structure."
                if (site.treeIsBuildable && site.hasStructureContentTemplate) { // || site.treeIsViewable) {
                    this.claims.add('CanViewSiteStructure');
                } else if (site.currentUserCanSeeStructure && site.hasStructureContentTemplate)
                    this.claims.add('CanViewSiteStructure');
            }

            //VNEXT-1618 enable access for non-admin editing
            if (site.currentUserCanEditSiteHomePageHtml) this.claims.add('CanViewSiteSummary');
            if (site.currentUserCanEditFormTemplateHtml) this.claims.add('CanEditFormTemplateHtmlOnly');
            if (site.currentUserCanBuildConstraints) this.claims.add('CanBuildConstraints');
            if (site.currentUserCanViewSiteOverview) this.claims.add('CanViewSiteOverview');
            if (site.currentUserCanBuildWorkflows) this.claims.add('CanBuildWorkflows');
            if (site.currentUserCanBuildWorkflows) this.claims.add('CanViewSiteWorkflowCatalog');
            if (site.currentUserCanPublish) this.claims.add('CanPublish');
            if (site.SiteHomePage?.isActive) {
                this.claims.add('CanViewSiteHomePage');
            }

            // VNEXT-1796 07-31-2025 note:  we cannot yet enable the following line as the 'Permissions'
            //                              page/component will need more granular control over what
            //                              users with only site-level permissions can do first.
            //if (site.canBuildAtLeastOneFolder) this.claims.add('CanViewSitePermissions');
        }
    }

    // Returns true if user has claim 
    public has(claim: string): boolean {
        // TODO: THIS IS TEMPORARY, REMOVE IT AFTER DEV
        if (environment.userGrantedAllClaims) {
            //console.warn("WARNING: UserClaims.has() is using environment.userGrantedAllClaims...");
            return true;
        }
        if (claim == null) return true;

        return this.claims.has(claim);
    }
}
